/**
 * Email Utilities for Document Writer
 *
 * Helper functions for email composition, validation, and formatting
 */

import type { Doc } from "@/convex/_generated/dataModel";

/**
 * Generate email subject line based on document type and metadata
 *
 * @param document - Document object with type, title, and metadata
 * @param companyName - Company name to include in subject (optional)
 * @returns Formatted subject line
 */
export function generateEmailSubject(
  document: {
    type?: string;
    title: string;
    metadata?: Record<string, any>;
  },
  companyName: string = "ManageClean OS"
): string {
  const docType = document.type?.toLowerCase() || "";

  switch (docType) {
    case "invoice": {
      const invoiceNo =
        document.metadata?.invoiceNumber || document.metadata?.["Invoice No."] || "N/A";
      return `Invoice No. ${invoiceNo} from ${companyName}`;
    }

    case "proposal": {
      const clientName =
        document.metadata?.clientName || document.metadata?.["Client Name"] || "Client";
      return `Proposal for ${clientName} - ${companyName}`;
    }

    case "quote": {
      const quoteNo = document.metadata?.quoteNumber || document.metadata?.["Quote No."] || "N/A";
      return `Quote No. ${quoteNo} from ${companyName}`;
    }

    case "service agreement":
    case "service-agreement":
      return `Service Agreement - ${companyName}`;

    case "contract":
      return `Contract - ${companyName}`;

    case "scope of work":
    case "scope-of-work":
      return `Scope of Work - ${companyName}`;

    default:
      // Use document title as fallback
      return document.title || `Document from ${companyName}`;
  }
}

/**
 * Validate email address format
 *
 * @param email - Email address to validate
 * @returns True if valid email format
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate multiple email addresses (comma-separated)
 *
 * @param emails - Comma-separated email addresses
 * @returns True if all emails are valid
 */
export function validateMultipleEmails(emails: string): boolean {
  if (!emails.trim()) return true; // Empty is valid (optional field)

  const emailList = emails.split(",").map((e) => e.trim());
  return emailList.every((email) => validateEmail(email));
}

/**
 * Convert PDF Blob to base64 string
 *
 * @param pdfBlob - PDF Blob to convert
 * @param filename - Filename for the PDF
 * @returns Promise with base64 data and metadata
 * @throws Error if file is too large (>35MB Gmail limit)
 */
export async function preparePdfAttachment(
  pdfBlob: Blob,
  filename: string
): Promise<{
  filename: string;
  content: string;
  mimeType: string;
  size: number;
}> {
  // Check file size (Gmail limit: 35MB)
  const maxSize = 35 * 1024 * 1024; // 35MB in bytes
  if (pdfBlob.size > maxSize) {
    throw new Error(
      `PDF file is too large for Gmail (${(pdfBlob.size / 1024 / 1024).toFixed(2)}MB). Maximum size is 35MB.`
    );
  }

  // Convert blob to base64
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      // Remove data URL prefix (data:application/pdf;base64,)
      const base64Data = base64String.split(",")[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(pdfBlob);
  });

  return {
    filename,
    content: base64,
    mimeType: "application/pdf",
    size: pdfBlob.size,
  };
}

/**
 * Format file size for display
 *
 * @param bytes - File size in bytes
 * @returns Formatted file size string (e.g., "2.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
}

/**
 * Generate default email body template
 *
 * @param documentType - Type of document being sent
 * @param clientName - Name of the client
 * @returns HTML email body template
 */
export function generateDefaultEmailBody(
  documentType: string,
  clientName: string = "Valued Client"
): string {
  const greeting = `<p>Dear ${clientName},</p>`;
  const closing = `<p>Best regards,<br>ManageClean OS Team</p>`;

  switch (documentType.toLowerCase()) {
    case "invoice":
      return `${greeting}<p>Please find attached your invoice. Payment is due as indicated on the invoice.</p><p>If you have any questions, please don't hesitate to contact us.</p>${closing}`;

    case "proposal":
      return `${greeting}<p>Thank you for considering our services. Please find attached our proposal for your review.</p><p>We look forward to the opportunity to work with you.</p>${closing}`;

    case "quote":
      return `${greeting}<p>Please find attached the quote you requested. This quote is valid for 30 days.</p><p>If you have any questions or would like to proceed, please let us know.</p>${closing}`;

    default:
      return `${greeting}<p>Please find the attached document for your review.</p><p>If you have any questions, please feel free to contact us.</p>${closing}`;
  }
}
